// Shiki highlighter, fine-grained + lazy so only the core, two themes, and a curated language set
// ship (each as its own dynamically-imported chunk). Uses the JS regex engine — no WASM. One shared
// highlighter instance is created on first use and reused across every CodeViewer.
import { createHighlighterCore, type HighlighterCore } from "shiki/core";
import { createJavaScriptRegexEngine } from "shiki/engine/javascript";

let instance: Promise<HighlighterCore> | null = null;

export function getHighlighter(): Promise<HighlighterCore> {
  instance ??= createHighlighterCore({
      themes: [
        import("@shikijs/themes/one-light"),
        import("@shikijs/themes/one-dark-pro"),
      ],
      langs: [
        import("@shikijs/langs/python"),
        import("@shikijs/langs/javascript"),
        import("@shikijs/langs/typescript"),
        import("@shikijs/langs/tsx"),
        import("@shikijs/langs/jsx"),
        import("@shikijs/langs/json"),
        import("@shikijs/langs/yaml"),
        import("@shikijs/langs/bash"),
        import("@shikijs/langs/markdown"),
        import("@shikijs/langs/sql"),
        import("@shikijs/langs/html"),
        import("@shikijs/langs/css"),
        import("@shikijs/langs/toml"),
        import("@shikijs/langs/go"),
        import("@shikijs/langs/rust"),
        import("@shikijs/langs/java"),
        import("@shikijs/langs/ruby"),
        import("@shikijs/langs/dockerfile"),
      ],
      engine: createJavaScriptRegexEngine(),
    });
  return instance;
}

// File extension → shiki language.
const EXT_LANG: Record<string, string> = {
  py: "python", js: "javascript", mjs: "javascript", cjs: "javascript",
  ts: "typescript", tsx: "tsx", jsx: "jsx", json: "json",
  yml: "yaml", yaml: "yaml", sh: "bash", bash: "bash", zsh: "bash",
  md: "markdown", markdown: "markdown", sql: "sql", html: "html", htm: "html",
  css: "css", toml: "toml", go: "go", rs: "rust", java: "java", rb: "ruby",
};

export function langFromPath(path: string): string {
  const name = (path.split("/").pop() ?? "").toLowerCase();
  if (name === "dockerfile") return "dockerfile";
  const ext = name.includes(".") ? name.split(".").pop()! : "";
  return EXT_LANG[ext] ?? "text";
}

// Common fence-info / alias → canonical, then fall back to plain text if we didn't load it.
const ALIASES: Record<string, string> = {
  js: "javascript", ts: "typescript", py: "python", sh: "bash", shell: "bash", zsh: "bash",
  yml: "yaml", rs: "rust", rb: "ruby", "": "text", txt: "text", plaintext: "text", plain: "text",
};

export function normalizeLang(lang: string | undefined, loaded: string[]): string {
  const l = (lang ?? "text").toLowerCase();
  const mapped = ALIASES[l] ?? l;
  if (mapped === "text") return "text";
  return loaded.includes(mapped) ? mapped : "text";
}
